import React, { useState, useEffect, useCallback } from "react";
import "../../assets/styles/TopNav.css";
import { ChevronDown } from "react-bootstrap-icons";
import bell from "../../assets/img/topnotification.svg";
import searchIcon from "../../assets/img/searchblack.svg";
import filterIcon from "../../assets/img/topfilter.svg";
import downarrow from "../../assets/img/downarrow.svg";
import { useNavigate, useLocation } from "react-router-dom";
import authService from "../../../services/authService";
import notificationService from "../../../services/notificationService";
import builderprofile2 from "../../assets/img/smallbuilderprofile.svg";

export default function TopNav() {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on pages that support search
  const isCRMPage = location.pathname === '/crm' || location.pathname === '/builder-crm';
  const isContactPage = location.pathname === '/builder-contacts' || location.pathname === '/contact';
  const isManageProjectPage = location.pathname === '/builder-manageproject' || location.pathname.includes('/account-settings/manage-project');
  const isSearchablePage = isCRMPage || isContactPage || isManageProjectPage;

  // Profile and notification state (matching mobile app)
  const [profile, setProfile] = useState({
    name: 'User',
    profileImage: 'https://i.pravatar.cc/40'
  });
  const [userProfile, setUserProfile] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [profileLoading, setProfileLoading] = useState(false);

  const API_BASE_URL = 'https://sandbox.rebax.in';

  // Helper function to truncate text if it exceeds maxLength
  const truncateText = (text, maxLength = 8) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Helper function to get full image URL (matching mobile app)
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${API_BASE_URL}${imagePath}`;
  };

  // Fetch user profile from API (matching mobile app)
  const fetchUserProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      const response = await authService.getProfile();
      if (response.success && response.data) {
        const apiUser = response.data.user || response.data;
        const profilePhoto = getFullImageUrl(apiUser.avatar_url || apiUser.profile_image || apiUser.avatar);
        const userProfileData = { ...apiUser, profilePhoto };
        setUserProfile(userProfileData);

        // Also update legacy profile state for compatibility
        setProfile({
          name: apiUser.full_name ||
                `${apiUser.first_name || ''} ${apiUser.last_name || ''}`.trim() ||
                apiUser.name ||
                'User',
          profileImage: profilePhoto || builderprofile2
        });

        console.log('✅ User profile fetched:', userProfileData);
        return userProfileData;
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    } finally {
      setProfileLoading(false);
    }
    return null;
  }, []);

  // Fetch notification count (matching mobile app)
  const fetchNotificationCount = useCallback(async () => {
    try {
      console.log('🔄 Fetching notification count...');
      const response = await notificationService.getUnreadCount();
      console.log('📦 Raw notification response:', response);
      console.log('📦 Response type:', typeof response);
      console.log('📦 Response success:', response?.success);
      console.log('📦 Response count:', response?.count);
      console.log('📦 Response count type:', typeof response?.count);

      // Check both response.count and response.data.count due to apiUtils wrapping
      const count = response?.count ?? response?.data?.count;
      console.log('🔍 Checking count in multiple locations:');
      console.log('  - response.count:', response?.count);
      console.log('  - response.data.count:', response?.data?.count);
      console.log('  - Final count value:', count);

      if (response && response.success && typeof count === 'number') {
        console.log('✅ Setting notification count to:', count);
        setNotificationCount(count);
        console.log('✅ Notification count state updated:', count);
        return count;
      } else {
        console.warn('⚠️ Invalid response format or no count found');
        console.warn('⚠️ Response success check:', response?.success);
        console.warn('⚠️ Count value:', count);
        console.warn('⚠️ Count type check:', typeof count);
        setNotificationCount(0);
      }
    } catch (err) {
      console.error('❌ Error fetching notification count:', err);
      console.error('❌ Error details:', err.message);
      console.error('❌ Error stack:', err.stack);
      setNotificationCount(0);
    }
    return 0;
  }, []);

  // Load profile on mount (matching mobile app)
  useEffect(() => {
    fetchUserProfile();
    fetchNotificationCount();
  }, [fetchUserProfile, fetchNotificationCount]);

  // Clear search when leaving searchable pages
  useEffect(() => {
    if (!isSearchablePage) {
      setSearchTerm("");
    }
  }, [isSearchablePage]);

  // Handle search change for all searchable pages
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Dispatch appropriate search events based on current page
    if (isCRMPage) {
      // Dispatch custom event that CRM.js can listen to
      window.dispatchEvent(
        new CustomEvent("crmSearch", { detail: value })
      );
    } else if (isContactPage) {
      // Dispatch custom event that Contact.js can listen to
      window.dispatchEvent(
        new CustomEvent("contactSearch", { detail: value })
      );
    } else if (isManageProjectPage) {
      // Dispatch custom event that ManageProject.js can listen to
      window.dispatchEvent(
        new CustomEvent("projectSearch", { detail: value })
      );
    }
  };

  return (
    <nav className="topnav1 navbar bg-white shadow-sm px-4 d-flex justify-content-between align-items-center">
      <div className=" d-flex align-items-center ">
        {/* Show search on supported pages */}
        {isSearchablePage && (
          <>
            <img src={searchIcon} alt="search" className="search-icon1" />
            <input
              type="search"
              className="form-control search-input1"
              placeholder={
                isCRMPage
                  ? "Search leads, invoices, projects..."
                  : isContactPage
                    ? "Search contacts..."
                    : isManageProjectPage
                      ? "Search projects..."
                      : "Search..."
              }
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </>
        )}
      </div>

      {/* Right Side */}
      <div className="d-flex align-items-center gap-3">
        {/* Notification with Badge (matching mobile app) */}
        <div
          onClick={() => {
            console.log('🔔 Notification clicked, current count:', notificationCount);
            navigate("/Notificationwebadmin");
            // Refresh notification count after navigating (like mobile app)
            setTimeout(() => {
              fetchNotificationCount();
            }, 1000);
          }}
          style={{ position: "relative", cursor: "pointer" }}
        >
          <img src={bell} alt="notification" width="48" height="48" />
          {notificationCount > 0 && (
            <div
              style={{
                position: "absolute",
                top: "-5px",
                right: "-5px",
                backgroundColor: "#FF3B30",
                borderRadius: "10px",
                minWidth: "20px",
                height: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingLeft: "4px",
                paddingRight: "4px",
                zIndex: 1000
              }}
            >
              <span
                style={{
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: "600",
                }}
              >
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            </div>
          )}
        </div>

        {/* Profile */}
        <div
          className="profile-pill d-flex align-items-center"
          onClick={() => {
            navigate("/adminprofile");
          }}
        >
          <img
            src={profile.profileImage}
            alt="profile"
            className="me-2"
            width="33"
            height="33"
            style={{ borderRadius: "8px" }}
            onError={(e) => {
              // Fallback if image fails to load
              e.target.src = "https://i.pravatar.cc/40?img=1";
            }}
          />
          <span className="profile-name"  style={{
                color: "white",}}>{truncateText(profile.name)}</span>
          <img src={downarrow} className="ms-2" size={20} />
        </div>
      </div>
    </nav>
  );
}
